import api from '@/modules/product/api'
import useRealtimePrices from '@/modules/product/useRealtimePrices'
import { BatchProductPriceUpdate } from '@/types/index'
import { defineQuery, defineQueryOptions, useQuery } from '@pinia/colada'
import { AxiosResponse } from 'axios'
import { ref, Ref } from 'vue'

type RealtimePricesBatchedQueryOptionsParams = {
  ids: Set<number>
  filters: string[] | undefined
}

type RealtimePricesDetailedQueryOptionsParams = {
  id: number
  filters: Ref<string[] | undefined>
}

type RealtimePricesBatchedQueryResult = {
  params: Ref<RealtimePricesBatchedQueryOptionsParams | undefined>
  data: Ref<AxiosResponse<BatchProductPriceUpdate[], unknown, object> | undefined>
  asyncStatus: Ref<'idle' | 'loading' | 'success' | 'error'>
  refresh: () => void
}

/**
 * Query keys for realtime price requests.
 *
 * The keys describe which realtime price data is stored in the Pinia Colada cache.
 * IDs and filters are normalized so that equal requests use the same cache entry,
 * independent of the original order of IDs or filters.
 */

const REALTIME_PRICES_QUERY_KEYS = {
  root: ['realtimePrices'] as const,
  normalizedIds: (ids: Set<number>) => Array.from(ids).sort((a, b) => a - b),
  normalizedFilters: (filters: string[] | undefined) => [...(filters ?? [])].sort(),
  batchFiltered: ({ ids, filters }: { ids: Set<number>; filters: string[] | undefined }) =>
    [
      ...REALTIME_PRICES_QUERY_KEYS.root,
      'batch',
      {
        ids: REALTIME_PRICES_QUERY_KEYS.normalizedIds(ids),
        filters: REALTIME_PRICES_QUERY_KEYS.normalizedFilters(filters)
      }
    ] as const,

  detailedById: (id: number, filters: string[] | undefined) =>
    [
      ...REALTIME_PRICES_QUERY_KEYS.root,
      'detailed',
      {
        id,
        filters: REALTIME_PRICES_QUERY_KEYS.normalizedFilters(filters)
      }
    ] as const
}

/**
 * Query options for loading realtime prices for multiple products.
 *
 * This variant is used when the product IDs and filters are already known
 * at the time the query options are created.
 */

const realtimePricesBatchedQueryOptions = defineQueryOptions(
  ({ ids, filters }: RealtimePricesBatchedQueryOptionsParams) => ({
    key: REALTIME_PRICES_QUERY_KEYS.batchFiltered({ ids, filters }),
    query: () => {
      const requestIds = new Set(ids)
      useRealtimePrices().addColadaLoading(requestIds)

      const productIds = REALTIME_PRICES_QUERY_KEYS.normalizedIds(requestIds)
      const normalizedFilters = REALTIME_PRICES_QUERY_KEYS.normalizedFilters(filters)

      return api
        .loadRealtimePricesBatch({
          productIds,
          scale: productIds.length,
          filters: normalizedFilters
        })
        .finally(() => {
          useRealtimePrices().removeColadaLoading(requestIds)
        })
    },
    meta: {
      onError: {
        defaultConfigParams: {
          appearance: 'NONE'
        }
      }
    }
  })
)

/**
 * Composable for batched realtime price loading.
 *
 * It exposes reactive parameters, the latest response data, the async status
 * and a refresh function. Loading state is handled by the query functions
 * while the backend requests are running.
 */

const realtimePricesBatchedQuery = defineQuery((): RealtimePricesBatchedQueryResult => {
  const params = ref<RealtimePricesBatchedQueryOptionsParams>({ ids: new Set(), filters: [] })

  const { data, asyncStatus, refresh } = useQuery({
    key: () => REALTIME_PRICES_QUERY_KEYS.batchFiltered(params.value),
    enabled: () => params.value.ids.size > 0,
    query: () => {
      const requestIds = new Set(params.value.ids)
      useRealtimePrices().addColadaLoading(requestIds)
      const productIds = REALTIME_PRICES_QUERY_KEYS.normalizedIds(requestIds)
      const normalizedFilters = REALTIME_PRICES_QUERY_KEYS.normalizedFilters(params.value.filters)
      return api
        .loadRealtimePricesBatch({
          productIds,
          scale: productIds.length,
          filters: normalizedFilters
        })
        .finally(() => {
          useRealtimePrices().removeColadaLoading(requestIds)
        })
    },
    meta: {
      onError: {
        defaultConfigParams: {
          appearance: 'NONE'
        }
      }
    }
  })

  return { params, data, asyncStatus, refresh }
})

/**
 * Query options for loading the realtime price of a single product.
 *
 * This is used on pages where only one product price has to be refreshed,
 * for example on a product detail page.
 */

const realtimePricesDetailedQueryOptions = defineQueryOptions(
  ({ id, filters }: RealtimePricesDetailedQueryOptionsParams) => ({
    key: REALTIME_PRICES_QUERY_KEYS.detailedById(id, filters.value),
    query: () => {
      const requestIds = new Set([id])
      useRealtimePrices().addColadaLoading(requestIds)

      const normalizedFilters = REALTIME_PRICES_QUERY_KEYS.normalizedFilters(filters.value)

      return api
        .loadRealtimePrices({
          productId: id,
          scale: 1,
          filters: normalizedFilters
        })
        .finally(() => {
          useRealtimePrices().removeColadaLoading(requestIds)
        })
    },
    meta: {
      onError: {
        defaultConfigParams: {
          appearance: 'NONE'
        }
      }
    }
  })
)

export {
  REALTIME_PRICES_QUERY_KEYS,
  realtimePricesBatchedQuery,
  realtimePricesBatchedQueryOptions,
  realtimePricesDetailedQueryOptions
}
export type { RealtimePricesBatchedQueryOptionsParams, RealtimePricesDetailedQueryOptionsParams }
